/**
 * CalendarView - Month Grid Calendar Component
 *
 * Displays events in a monthly calendar grid format.
 * Supports navigation between months and clicking on days.
 *
 * Issue #1059: Visual calendar view for club events
 */

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarEvent {
  id: string;
  title: string;
  starts_at: number; // Unix timestamp in seconds
}

interface CalendarViewProps {
  events: CalendarEvent[];
  onDayClick: (date: Date, dayEvents: CalendarEvent[]) => void;
  initialMonth?: Date;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Get all days in a month view (including padding days from prev/next months)
 */
function getMonthDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days: Date[] = [];

  // Add days from previous month to fill first week
  const firstWeekday = firstDay.getDay();
  for (let i = firstWeekday - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }

  // Add all days of current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  // Add days from next month to fill last week
  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      days.push(new Date(year, month + 1, i));
    }
  }

  return days;
}

/**
 * Check if two dates are the same calendar day
 */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Check if a date is today
 */
function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export default function CalendarView({ events, onDayClick, initialMonth }: CalendarViewProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialMonth) return initialMonth;
    return new Date();
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // Get all days for the current month view
  const days = useMemo(() => getMonthDays(year, month), [year, month]);

  // Create a map of date string -> events for that day
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();

    for (const event of events) {
      const eventDate = new Date(event.starts_at * 1000);
      const key = `${eventDate.getFullYear()}-${eventDate.getMonth()}-${eventDate.getDate()}`;
      const existing = map.get(key) || [];
      existing.push(event);
      map.set(key, existing);
    }

    return map;
  }, [events]);

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return eventsByDate.get(key) || [];
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const monthLabel = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl overflow-hidden">
      {/* Header with navigation */}
      <div className="flex items-center justify-between p-4 border-b border-oro-border">
        <button
          onClick={goToPrevMonth}
          className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5 text-text-secondary" />
        </button>

        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-text-primary">{monthLabel}</h2>
          <button
            onClick={goToToday}
            className="text-xs text-accent hover:underline"
          >
            Today
          </button>
        </div>

        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5 text-text-secondary" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-oro-border">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-medium text-text-muted"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {days.map((date, i) => {
          const dayEvents = getEventsForDate(date);
          const isCurrentMonth = date.getMonth() === month;
          const isTodayDate = isToday(date);
          const hasEvents = dayEvents.length > 0;

          return (
            <button
              key={i}
              onClick={() => onDayClick(date, dayEvents)}
              className={`
                relative min-h-[60px] p-2 border-b border-r border-oro-border
                transition-colors
                ${isCurrentMonth ? 'bg-transparent' : 'bg-oro-surface-alt/50'}
                ${hasEvents ? 'hover:bg-accent/10' : 'hover:bg-oro-surface-alt'}
                ${i % 7 === 6 ? 'border-r-0' : ''}
              `}
            >
              {/* Day number */}
              <span
                className={`
                  flex items-center justify-center w-7 h-7 text-sm font-medium rounded-full
                  ${isTodayDate ? 'bg-accent text-black' : ''}
                  ${!isTodayDate && isCurrentMonth ? 'text-text-primary' : ''}
                  ${!isTodayDate && !isCurrentMonth ? 'text-text-muted' : ''}
                `}
              >
                {date.getDate()}
              </span>

              {/* Event indicators */}
              {hasEvents && (
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {dayEvents.slice(0, 3).map((_, idx) => (
                    <span
                      key={idx}
                      className="w-1.5 h-1.5 rounded-full bg-accent"
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <span className="text-[10px] text-accent font-medium ml-0.5">
                      +{dayEvents.length - 3}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
